import { MutationType } from '@tencent/slide-sdk-pb-model';
import { MutationSource } from '../constants/mutation-constants';
import { PresentationEventTypes } from '../manager/event-manager/event-types';
import { createTransactionProvider } from '../manager/event-manager/transaction-events';
import { getResourceMapKeyframe } from '../keyframe/get-resource-map-keyframe';
import { createMutations } from '../mutation/mutation-factory';
import { record } from '../utils/recorder';
import { SetDataExtensionMutation } from '../mutation/data-extension/set-data-extension-mutation';
import { RemoveDataExtensionMutation } from '../mutation/data-extension/remove-data-extension-mutation';
import { DataTypeUtils } from '../data';
import type { DataType } from '../data';
import type { IEventManager } from '../manager/event-manager';
import type { ITransactionProvider } from '../manager/event-manager/transaction-events';
import type { PresentationDataModel } from '../model/presentation-data-model';
import type { IBaseContext } from '../context/base-context';
import type { IPageContext } from '../context/page-context';
import type { Mutation, MaybeError } from '../mutation/base/base-mutation';
import type { MarkOptional } from 'ts-essentials';

type IMutationDataModel = {
    id: string;
    type: DataType;
    context?: IBaseContext;
};

export class MutationAPI<RelatedDataModel extends IMutationDataModel> {
    /**
     * 开启 Mutation 事务，合并多个 MutationRequest，支持同步嵌套事务。
     * 每层事务结束时，会集中应用暂存的 Mutation，最外层事务结束时，会触发变更通知。
     *
     * NOTE: 暂时不支持异步事务
     */
    public transaction: ITransactionProvider;

    public constructor(public model: RelatedDataModel, public events: IEventManager) {
        this.transaction = createTransactionProvider(events);
    }

    @record()
    public setDataExtension(key: string, value: string) {
        if (!key || !value) {
            throw new Error('key and value is required');
        }

        const ctx = this.model.context as MarkOptional<IPageContext, 'getPage'> | undefined;
        this.transaction(() => {
            this.request([
                new SetDataExtensionMutation({
                    targetType: DataTypeUtils.toTargetType(this.model.type),
                    pageId: ctx?.getPage?.().id,
                    targetId: this.model.id === this.getPresentation().id ? undefined : this.model.id,
                    key,
                    value,
                }),
            ]);
        });
    }

    @record()
    public removeDataExtension(key?: string) {
        const ctx = this.model.context as MarkOptional<IPageContext, 'getPage'> | undefined;
        this.transaction(() => {
            this.request([
                new RemoveDataExtensionMutation({
                    targetType: DataTypeUtils.toTargetType(this.model.type),
                    pageId: ctx?.getPage?.().id,
                    targetId: this.model.id === this.getPresentation().id ? undefined : this.model.id,
                    key,
                }),
            ]);
        });
    }

    public forEachInTransaction = <T>(items: T[], action: (item: T, i: number) => void) => {
        this.transaction(() => {
            for (const [index, item] of items.entries()) {
                action(item, index);
            }
        });
    };

    protected request(mutations: Mutation[], applyImmediately = false): Mutation[] {
        const schedule = applyImmediately ? this.transaction : (cb: () => void) => cb();
        schedule(() => {
            this.events.emit(
                PresentationEventTypes.EnqueueMutations,
                this.onBeforeEnqueueMutations(mutations),
                MutationSource.Editor,
            );
        });
        return mutations;
    }

    protected guard(checker: (presentation: PresentationDataModel) => MaybeError) {
        const presentation = this.getPresentation();
        if (!presentation) {
            throw new Error('Presentation is not ready or model has no context');
        }

        const err = checker(presentation as PresentationDataModel);
        if (err) {
            throw err;
        }
    }

    protected getPresentation() {
        const presentation = this.model.context?.getPresentation();
        if (!presentation) {
            throw new Error('Presentation is not ready or model has no context');
        }
        return presentation as PresentationDataModel;
    }

    private onBeforeEnqueueMutations(mutations: Mutation[]): Mutation[] {
        const enqueueMutations: Mutation[] = [];
        const mutsMaybeWithResource = new Set([
            MutationType.SetPresentationProperties,
            MutationType.SetPageProperties,
            MutationType.SetTableCellProperties,
            MutationType.SetTextProperties,
            MutationType.SetChartSpaceProperties,
            MutationType.SetSubChartProperties,
            MutationType.SetTextBodyProperties,
        ]);
        const mutsMaybeWithResourceAndInValidRectData = new Set([
            MutationType.SetShapeProperties,
            MutationType.SetTextData,
        ]);

        // 所有涉及媒体资源的 Mutation 都遍历所有属性，找到野生的 URL，添加到 ResourceMap 中
        for (const mutation of mutations) {
            const data = mutation.toData();

            if (mutsMaybeWithResourceAndInValidRectData.has(mutation.type)) {
                const isModifyData = this.getPresentation().modifyData(data);
                const resourceMap = this.getPresentation().findWildResourceMap(data, true);
                if (resourceMap.count) {
                    enqueueMutations.push(mutation.clone(data));
                    enqueueMutations.push(...createMutations(getResourceMapKeyframe(resourceMap)));
                    continue;
                }
                if (isModifyData) {
                    enqueueMutations.push(mutation.clone(data));
                    continue;
                }
            }
            if (mutsMaybeWithResource.has(mutation.type)) {
                const resourceMap = this.getPresentation().findWildResourceMap(data, true);
                if (resourceMap.count) {
                    enqueueMutations.push(mutation.clone(data));
                    enqueueMutations.push(...createMutations(getResourceMapKeyframe(resourceMap)));
                    continue;
                }
            }
            enqueueMutations.push(mutation);
        }

        return enqueueMutations;
    }
}
